import { useForm } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { assignPSCToCampaignRequest } from '~/services/campaign';

const schema = yup.object().shape({
    patientServicesCoordinator: yup
        .object({
            value: yup
                .mixed<string | number>()
                .required('Client Services Coordinator is required'),
            label: yup
                .string()
                .required('Client Services Coordinator is required'),
        })
        .required('Client Services Coordinator is required'),
});

interface Option {
    value: string | number;
    label: string;
}

interface CampaignRequest {
    id: number;
    patient_services_coordinator?: {
        person: {
            id: number;
            first_name: string;
            last_name: string;
        };
    };
}

interface CSCAssignmentDrawerProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    campaignRequestId?: number;
    campaignRequest?: CampaignRequest;
    onCampaignRequestUpdate?: (updatedCampaignRequest: CampaignRequest) => void;
    metadata?: {
        patient_services_coordinators?: Array<{
            id: number;
            full_name: string;
        }>;
    };
}

const CSCAssignmentDrawer = ({
    isOpen,
    setIsOpen,
    campaignRequestId,
    campaignRequest,
    onCampaignRequestUpdate,
    metadata,
}: CSCAssignmentDrawerProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Convert metadata CSC list to Select options
    const allCSCOptions = useMemo(() => {
        if (!metadata?.patient_services_coordinators) {
            return [];
        }
        return metadata.patient_services_coordinators.map((psc) => ({
            value: psc.id,
            label: psc.full_name,
        }));
    }, [metadata]);

    const getDefaultValues = useCallback(() => {
        const baseDefaults = {
            patientServicesCoordinator: {
                value: '',
                label: 'Select a coordinator...',
            },
        };

        if (campaignRequest?.patient_services_coordinator?.person) {
            const coordinator =
                campaignRequest.patient_services_coordinator.person;
            baseDefaults.patientServicesCoordinator = {
                value: String(coordinator.id),
                label: `${coordinator.first_name} ${coordinator.last_name}`,
            };
        }
        return baseDefaults;
    }, [campaignRequest]);

    const {
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: getDefaultValues(),
        resolver: yupResolver(schema),
    });

    const selectedCoordinator = watch('patientServicesCoordinator');

    useEffect(() => {
        reset(getDefaultValues());
    }, [campaignRequest, reset, getDefaultValues]);

    const onSubmit = async (data: any) => {
        if (!campaignRequestId) {
            toast.error(
                'Campaign Request ID is missing. Please refresh the page and try again.',
            );
            return;
        }

        setIsSubmitting(true);
        toast.loading('Assigning coordinator...');
        try {
            const response = await assignPSCToCampaignRequest(
                campaignRequestId,
                Number(data.patientServicesCoordinator.value),
            );
            toast.dismiss();
            toast.success('Coordinator assigned successfully');

            if (onCampaignRequestUpdate && response) {
                onCampaignRequestUpdate(response);
            }

            setIsOpen(false);
        } catch (e: any) {
            toast.dismiss();
            console.error('Error assigning PSC:', e);
            toast.error(
                e?.detail || 'Error assigning coordinator. Please try again.',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        reset(getDefaultValues());
    };

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title='Assign Client Services Coordinator'
        >
            <div className=''>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='mb-6'>
                        <label className='mb-2 block text-sm font-medium text-gray-700'>
                            Client Services Coordinator
                        </label>
                        <Select
                            menuPlacement='auto'
                            value={selectedCoordinator}
                            onChange={(newValue) => {
                                setValue(
                                    'patientServicesCoordinator',
                                    newValue as Option,
                                    { shouldValidate: true },
                                );
                            }}
                            options={allCSCOptions}
                            placeholder='Search for a coordinator...'
                            noOptionsMessage={() => 'No coordinators found'}
                            isClearable
                            isSearchable
                            filterOption={(option, inputValue) => {
                                if (!inputValue) return true;
                                return option.label
                                    .toLowerCase()
                                    .includes(inputValue.toLowerCase());
                            }}
                            getOptionLabel={(option: Option) => option.label}
                            getOptionValue={(option: Option) =>
                                String(option.value)
                            }
                            className='react-select-container'
                            classNamePrefix='react-select'
                        />
                        {errors.patientServicesCoordinator && (
                            <span className='text-red-500 mt-1 block text-sm'>
                                {errors.patientServicesCoordinator.message ||
                                    (errors.patientServicesCoordinator as any)
                                        ?.value?.message}
                            </span>
                        )}
                    </div>

                    <div className='bg-blue-50 mb-6 rounded p-4 text-sm text-gray-600'>
                        <p className='mb-2 font-medium'>Note:</p>
                        <p>
                            Assigning a Client Services Coordinator to this
                            Campaign Request will:
                        </p>
                        <ul className='mt-2 list-inside list-disc space-y-1'>
                            <li>
                                Update the Campaign Request with the selected
                                coordinator
                            </li>
                            <li>
                                If the associated Campaign has the same
                                coordinator or no coordinator assigned, it will
                                also be updated
                            </li>
                        </ul>
                    </div>

                    <div className='flex justify-end space-x-3 border-t pt-4'>
                        <button
                            type='button'
                            onClick={handleClose}
                            className='rounded border border-gray-300 px-4 py-2 text-black'
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            disabled={isSubmitting}
                            style={{
                                backgroundColor: isSubmitting
                                    ? 'rgb(156 163 175)'
                                    : 'rgb(234 179 8)',
                            }}
                            className='rounded px-4 py-2 text-white'
                        >
                            {isSubmitting
                                ? 'Assigning...'
                                : 'Assign Coordinator'}
                        </button>
                    </div>
                </form>
            </div>
        </Drawer>
    );
};

export default CSCAssignmentDrawer;
